import { useMemo, useState } from 'react';

import { Trans } from '@lingui/react/macro';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
  Command,
  CommandList,
} from '@repo/videa-components/src/index';

import type { ZuubOnboardingStatusResponse } from '@/@types/zuub/contracts';
import { useZuubRcmOnboardingPayers } from '@/react-query/rcm/useZuubRcmOnboarding';
import { useDebounce } from '@/utils/hooks/useDebounce';

import { PayerCountAndSort } from './subcomponents/PayerCountAndSort';
import { PayerList } from './subcomponents/PayerList';
import { PayerListItem } from './subcomponents/PayerListItem';
import { SearchAndAddPayer } from './subcomponents/SearchAndAddPayer';

interface PayerListSidebarProps {
  selectedPayerId?: number | null;
  testingPayerIds?: Set<number>;
  payerStatusData?: Record<string, ZuubOnboardingStatusResponse>;
  /**
   * Map of payer IDs to their loading states.
   * Example: { "145": true, "200": false, "300": true }
   * - Key: payer ID as string (e.g., "145")
   * - Value: boolean indicating if that payer's status is currently being fetched
   * Used to show loading spinners for individual payers while their status is loading.
   */
  loadingStates?: Record<string, boolean>;
  onPayerClick?: (payerId: number) => void;
}

export const PayerListSidebar = ({
  selectedPayerId,
  testingPayerIds = new Set(),
  payerStatusData,
  loadingStates = {},
  onPayerClick,
}: PayerListSidebarProps) => {
  // state
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('asc');
  const [searchQuery, setSearchQuery] = useState('');

  // hooks
  const { data: payers, isLoading } = useZuubRcmOnboardingPayers();
  const { debouncedValue: debouncedSearch } = useDebounce(searchQuery);

  const sortedPayers = useMemo(() => {
    if (!payers) return [];
    const sorted = [...payers].sort((a, b) => {
      const nameA = a.name.toLowerCase();
      const nameB = b.name.toLowerCase();
      if (sortOrder === 'asc') {
        return nameA.localeCompare(nameB);
      } else {
        return nameB.localeCompare(nameA);
      }
    });
    return sorted;
  }, [payers, sortOrder]);

  const filteredPayers = useMemo(() => {
    if (!debouncedSearch.trim()) {
      return sortedPayers;
    }
    const searchLower = debouncedSearch.toLowerCase();
    return sortedPayers.filter((payer) =>
      payer.name.toLowerCase().includes(searchLower),
    );
  }, [sortedPayers, debouncedSearch]);

  const topPayers = useMemo(() => {
    if (!payers) return [];
    return payers
      .filter((payer) => payer.rank != null)
      .sort((a, b) => (a.rank ?? 0) - (b.rank ?? 0))
      .slice(0, 10);
  }, [payers]);

  // Calculate connected count for top payers
  const topPayersConnectedCount = useMemo(() => {
    return topPayers.filter((payer) => {
      const status = payerStatusData?.[payer.id.toString()];
      return status?.credentialStatus === 'Succeeded';
    }).length;
  }, [topPayers, payerStatusData]);

  const toggleSort = () => {
    setSortOrder((prev) => (prev === 'asc' ? 'desc' : 'asc'));
  };

  const handleGoToPayerPortal = (portalUrl: string | undefined) => {
    if (portalUrl) {
      window.open(portalUrl, '_blank', 'noopener,noreferrer');
    }
  };

  if (isLoading) {
    return (
      <div className="vai-flex vai-items-center vai-justify-center vai-p-8">
        <div className="vai-text-gray-500">Loading payers...</div>
      </div>
    );
  }

  if (!payers || payers.length === 0) {
    return (
      <div className="vai-flex vai-items-center vai-justify-center vai-p-8">
        <div className="vai-text-gray-500">No payers available</div>
      </div>
    );
  }

  return (
    <div className="vai-flex vai-flex-col vai-h-full vai-bg-white">
      <SearchAndAddPayer
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
      />

      <div className="vai-px-6 vai-border-b">
        {/* Top Payers Accordion */}
        <Accordion type="single" collapsible>
          <AccordionItem value="top-payers">
            <AccordionTrigger className="vai-text-sm vai-text-muted-foreground vai-py-0 vai-font-normal hover:vai-no-underline vai-gap-2">
              <div className="vai-flex vai-items-center vai-gap-2 vai-flex-1 vai-justify-between">
                <span>
                  <Trans>Top Payers</Trans>
                </span>
                <div className="vai-flex vai-items-center vai-gap-2 vai-ml-auto">
                  <span className="vai-text-muted-foreground vai-whitespace-nowrap">
                    {topPayersConnectedCount}/{topPayers.length} connected
                  </span>
                  <div className="vai-w-12 vai-h-3 vai-bg-gray-200 vai-rounded-sm vai-overflow-hidden">
                    <div
                      className="vai-h-full vai-bg-green-500 vai-transition-all"
                      style={{
                        width: `${
                          topPayers.length > 0
                            ? (topPayersConnectedCount / topPayers.length) * 100
                            : 0
                        }%`,
                      }}
                    />
                  </div>
                </div>
              </div>
            </AccordionTrigger>
            <AccordionContent className="vai-pb-4">
              <Command className="vai-bg-transparent vai-border-0">
                <CommandList className="vai-p-0">
                  {topPayers.map((payer) => {
                    const isSelected = selectedPayerId === payer.id;
                    const payerStatus = payerStatusData?.[payer.id.toString()];
                    const isActivelyTesting = testingPayerIds.has(payer.id);
                    const isLoading =
                      loadingStates?.[payer.id.toString()] ?? false;

                    return (
                      <PayerListItem
                        key={payer.id}
                        payer={payer}
                        isSelected={isSelected}
                        payerStatus={payerStatus}
                        isActivelyTesting={isActivelyTesting}
                        isLoading={isLoading}
                        onSelect={() => onPayerClick?.(payer.id)}
                        onGoToPortal={handleGoToPayerPortal}
                      />
                    );
                  })}
                </CommandList>
              </Command>
            </AccordionContent>
          </AccordionItem>
        </Accordion>
      </div>

      <PayerCountAndSort
        count={filteredPayers.length}
        sortOrder={sortOrder}
        onSortToggle={toggleSort}
      />
      {/* All Payers List */}
      <PayerList
        payers={filteredPayers}
        selectedPayerId={selectedPayerId}
        testingPayerIds={testingPayerIds}
        payerStatusData={payerStatusData}
        loadingStates={loadingStates}
        onPayerClick={onPayerClick || (() => {})}
        onGoToPortal={handleGoToPayerPortal}
      />
    </div>
  );
};
